'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import { toSnakeCase } from 'DesignData/lib/text';
import { isNullOrUndefined } from 'ContentComponents/cmv2/utils';
import ToolFormControl from 'DesignComponents/tool/ToolFormControl';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import Small from 'UIComponents/elements/Small';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UITextInput from 'UIComponents/input/UITextInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import ChoiceSelect from 'ContentComponents/cmv2/fields/Choice';
import DisplayTypes from 'ContentUtils/constants/CustomWidgetFieldDisplayTypes';
import { CHOICE } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { CHOICES, DEFAULT, DISPLAY, MULTIPLE, PLACEHOLDER, REORDERING_ENABLED } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { MODULE_VALIDATION_CHOICE_DUP_VALUE } from 'DesignData/enums/CustomWidgetFieldErrorTypes';
import IconLink from 'DesignEditorsUI/components/shared/IconLink';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import ChoiceOption from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/contentOptions/ChoiceOption';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

function asMutable(list) {
  if (isNullOrUndefined(list)) return '';
  return list.asMutable ? list.asMutable() : list;
}

class ChoiceContentOptions extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      newChoiceAdded: false
    };

    this.setChoiceContainerRef = ref => {
      this._choiceContainer = ref;
    };

    this.focusLastChoice = () => {
      const choiceOptions = findDOMNode(this._choiceContainer).children;

      if (choiceOptions) {
        choiceOptions[choiceOptions.length - 1].querySelector('.choice-label-input').focus();
      }
    };

    this.onDefaultChange = value => {
      this.props.editField({
        default: value
      });
    };

    this.onDeleteChoice = index => {
      const {
        field,
        editField
      } = this.props;
      const {
        choices
      } = field;

      if (!choices[index] || choices.length === 1) {
        return;
      }

      const removedValue = choices[index][0];
      const newChoices = choices.slice(0, index).concat(choices.slice(index + 1));
      let newDefault = field[DEFAULT] === removedValue ? newChoices[0][0] : field[DEFAULT];

      if (field[MULTIPLE] && Array.isArray(field[DEFAULT]) && field[DEFAULT].length) {
        newDefault = field[DEFAULT].filter(val => val !== removedValue);
      }

      editField({
        [CHOICES]: newChoices,
        [DEFAULT]: newDefault
      });
    };

    this.onChoiceLabelChange = (index, e) => {
      const {
        field,
        editField
      } = this.props;
      const {
        newChoiceAdded
      } = this.state;
      const choices = field.choices.map((choice, i) => {
        if (i === index) {
          const label = I18n.SafeString(e.target.value).string;
          const newValue = newChoiceAdded ? toSnakeCase(label) : choice[0];
          return [newValue, label];
        }

        return choice;
      });
      editField({
        choices
      });
    };

    this.onChoiceLabelBlur = () => {
      this.setState({
        newChoiceAdded: false
      });
    };

    this.onChoiceValueChange = (index, e) => {
      const {
        field,
        editField
      } = this.props;
      const {
        choices
      } = field;
      const value = e.target.value;
      const isDefault = choices[index][0] === field.default;
      let newDefault = field[DEFAULT];
      const newChoices = [...choices];
      newChoices[index] = [value, choices[index][1]];

      if (field[MULTIPLE] && Array.isArray(field[DEFAULT]) && field[DEFAULT].length) {
        newDefault = field[DEFAULT].map(val => {
          if (val === choices[index][0]) {
            return value;
          }

          return val;
        });
      }

      if (isDefault) {
        newDefault = value;
      }

      const changes = {
        [CHOICES]: newChoices,
        [DEFAULT]: newDefault
      };
      editField(changes);
    };

    this.onAddChoice = () => {
      const {
        field: {
          choices
        },
        editField
      } = this.props;
      editField({
        [CHOICES]: choices.concat([['', '']])
      });
      this.setState({
        newChoiceAdded: true
      });
    };

    this.onMoveChoice = (oldIndex, newIndex, persist = false) => {
      const {
        field: {
          choices
        },
        editField
      } = this.props;
      const choice = choices[oldIndex];
      let reorderedChoices = choices.slice(0, oldIndex).concat(choices.slice(oldIndex + 1));
      reorderedChoices = reorderedChoices.slice(0, newIndex).concat([choice], reorderedChoices.slice(newIndex));
      editField({
        [CHOICES]: reorderedChoices
      }, persist);
    };

    this.onPlaceholderChange = e => {
      this.props.editField({
        [PLACEHOLDER]: e.target.value
      });
    };

    this.onMultipleChange = e => {
      const {
        editField,
        field
      } = this.props;
      const isChecked = e.target.checked;
      const updatedProps = {
        [MULTIPLE]: isChecked
      };

      if (isChecked && field[REORDERING_ENABLED] && field[DISPLAY] !== DisplayTypes[CHOICE].SELECT) {
        updatedProps[DISPLAY] = DisplayTypes[CHOICE].SELECT;
      } else if (isChecked && field[DISPLAY] === DisplayTypes[CHOICE].RADIO) {
        updatedProps[DISPLAY] = DisplayTypes[CHOICE].CHECKBOX;
      } else if (!isChecked && field[DISPLAY] === DisplayTypes[CHOICE].CHECKBOX) {
        updatedProps[DISPLAY] = DisplayTypes[CHOICE].RADIO;
      }

      if (isChecked && !Array.isArray(field[DEFAULT])) {
        updatedProps[DEFAULT] = isNullOrUndefined(field[DEFAULT]) || field[DEFAULT] === '' ? [] : [field[DEFAULT]];
      } else if (!isChecked && Array.isArray(field[DEFAULT])) {
        updatedProps[DEFAULT] = field[DEFAULT][0] === undefined ? '' : field[DEFAULT][0];
      }

      editField(updatedProps);
    };

    this.onReorderingChange = e => {
      const {
        editField,
        field
      } = this.props;
      const isChecked = e.target.checked;
      const updatedProps = {
        [REORDERING_ENABLED]: isChecked
      };

      if (isChecked && field[DISPLAY] === DisplayTypes[CHOICE].CHECKBOX) {
        updatedProps[DISPLAY] = DisplayTypes[CHOICE].SELECT;
      }

      editField(updatedProps);
    };

    this.onDisplayChange = e => {
      this.props.editField({
        [DISPLAY]: e.target.name
      });
    };

    this.getChoiceValueDuplicates = () => {
      const choiceErrors = this.props.fieldErrors[CHOICES] || [];
      return choiceErrors.reduce((acc, error) => {
        const {
          type,
          data
        } = error;

        if (type === MODULE_VALIDATION_CHOICE_DUP_VALUE) {
          return Object.assign({}, acc, {
            [data.value]: error
          });
        }

        return acc;
      }, {});
    };

    this.getNonDuplicateErrors = () => {
      const choiceErrors = this.props.fieldErrors[CHOICES] || [];
      return choiceErrors.filter(({
        type
      }) => type !== MODULE_VALIDATION_CHOICE_DUP_VALUE);
    };
  }

  componentDidUpdate() {
    if (this.state.newChoiceAdded) {
      this.focusLastChoice();
    }
  }

  renderChoices() {
    const {
      field: {
        choices
      },
      readOnly
    } = this.props;
    const choiceValueDuplicates = this.getChoiceValueDuplicates();
    const deletable = choices.length > 2 && !readOnly;
    return choices.map((choice, index) => {
      const [value, label] = choice;
      const duplicateError = choiceValueDuplicates[value];
      return /*#__PURE__*/_jsx(ChoiceOption, {
        deletable: deletable,
        duplicateError: duplicateError,
        index: index,
        label: label,
        onDelete: (...partialArgs) => this.onDeleteChoice(index, ...partialArgs),
        onLabelChange: (...partialArgs) => this.onChoiceLabelChange(index, ...partialArgs),
        onMove: this.onMoveChoice,
        onValueChange: (...partialArgs) => this.onChoiceValueChange(index, ...partialArgs),
        value: value,
        onLabelBlur: this.onChoiceLabelBlur,
        readOnly: readOnly
      }, index);
    });
  }

  renderMultipleToggle() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(UICheckbox, {
      checked: field[MULTIPLE],
      className: "p-y-8",
      onChange: this.onMultipleChange,
      readOnly: readOnly,
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('choice.labels.allowMultiple')
      })
    });
  }

  renderReorderingToggle() {
    const {
      field,
      readOnly
    } = this.props;
    if (!field[MULTIPLE]) return null;
    return /*#__PURE__*/_jsx(UICheckbox, {
      checked: field[REORDERING_ENABLED],
      className: "p-bottom-6",
      onChange: this.onReorderingChange,
      readOnly: readOnly,
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('choice.labels.allowReordering')
      })
    });
  }

  renderDisplayOptions() {
    const {
      field
    } = this.props;
    const isMultiple = field[MULTIPLE];
    const radios = Object.values(DisplayTypes[CHOICE]).map(displayType => {
      const checked = field[DISPLAY] === displayType;
      let disabled;

      if (displayType === DisplayTypes[CHOICE].RADIO && isMultiple) {
        disabled = true;
      }

      if (displayType === DisplayTypes[CHOICE].CHECKBOX && (!isMultiple || field[REORDERING_ENABLED])) {
        disabled = true;
      } // Remove buttons logic once they are available in the choice field


      if (displayType === DisplayTypes[CHOICE].BUTTONS) {
        return null;
      }

      return /*#__PURE__*/_jsx(UIRadioInput, {
        checked: checked,
        className: "content-options-radio-input",
        disabled: disabled,
        inline: true,
        name: displayType,
        onChange: this.onDisplayChange,
        size: "small",
        children: text(i18nKey(`display.displayTypeLabels.${displayType}`))
      }, displayType);
    });
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('display.label')
      }),
      children: /*#__PURE__*/_jsx(Fragment, {
        children: radios
      })
    });
  }

  render() {
    const nonDuplicateErrors = this.getNonDuplicateErrors();
    const {
      field,
      isV1,
      readOnly
    } = this.props;

    const choicesLabel = /*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsx("span", {
        className: "m-right-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('choice.labels.choices')
        })
      }), /*#__PURE__*/_jsx(UIHelpIcon, {
        title: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('choice.htmlUsageTooltip')
          })
        }),
        color: BATTLESHIP,
        tooltipPlacement: "top"
      })]
    });

    const placeholderLabel = /*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsx("span", {
        className: "m-right-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('choice.labels.placeholder')
        })
      }), /*#__PURE__*/_jsx(UIHelpIcon, {
        title: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('choice.placeholderTooltip')
          })
        }),
        color: BATTLESHIP,
        tooltipProps: {
          placement: 'top'
        }
      })]
    });

    const placeholderInput = !isV1 && /*#__PURE__*/_jsx(ToolFormControl, {
      label: placeholderLabel,
      children: /*#__PURE__*/_jsx(UITextInput, {
        onChange: this.onPlaceholderChange,
        value: field[PLACEHOLDER],
        disabled: readOnly
      })
    });

    return /*#__PURE__*/_jsx("div", {
      className: "choice-content-options",
      children: /*#__PURE__*/_jsxs(ToolFieldset, {
        children: [/*#__PURE__*/_jsx(ToolFormControl, {
          label: text(i18nKey('choice.labels.default')),
          children: /*#__PURE__*/_jsx(ChoiceSelect, {
            choices: field[CHOICES],
            display: field[DISPLAY],
            multiple: field[MULTIPLE],
            onFieldChange: this.onDefaultChange,
            placeholder: field[PLACEHOLDER],
            readOnly: readOnly,
            reordering_enabled: field[REORDERING_ENABLED],
            required: false,
            value: asMutable(field[DEFAULT])
          })
        }), this.renderMultipleToggle(), this.renderReorderingToggle(), this.renderDisplayOptions(), /*#__PURE__*/_jsx(FormControlWithErrors, {
          errors: nonDuplicateErrors,
          label: choicesLabel,
          children: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx("div", {
              ref: this.setChoiceContainerRef,
              children: this.renderChoices()
            }), /*#__PURE__*/_jsx(IconLink, {
              className: "m-top-3",
              name: "add",
              onClick: this.onAddChoice,
              readOnly: readOnly,
              children: /*#__PURE__*/_jsx("span", {
                className: "m-left-1",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: i18nKey('choice.addChoice')
                })
              })
            })]
          })
        }), placeholderInput]
      })
    });
  }

}

ChoiceContentOptions.propTypes = {
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  isV1: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool
};
ChoiceContentOptions.defaultProps = {
  fieldErrors: {}
};
export default ChoiceContentOptions;